import { FeatureFlag, TrackingEvent, DbRef, SchedulingFilter, orderedMedicalTitleRanking } from '@caresend/types';
import { getOfficePath, getMinimumTitleForProcedures, deduplicateArray, SECOND } from '@caresend/utils';
import 'firebase/auth';
import { calculateDraftWaypointVisitPriceRequest } from '../../../../database/firebaseConfig/cloudFunctions.js';
import '../../../../database/firebaseConfig/init.js';
import { getValueOnce, getValuesOnce } from '../../../../database/firebaseConfig/methods.js';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import '../../../../components/ui/Icon.vue.js';
import '../../../../functions/configuration/icons.js';
import '../../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../../functions/tracking/index.js';
import { trackEvent } from '../../../../functions/tracking/tracking.js';
import { getStore } from '../../../helpers/initStore.js';
import '@firebase/auth';
import 'browser-image-compression';
import 'firebase/storage';
import 'js-sha256';
import { getCollectionFilteredTimeSlots } from '../filters/collection.js';
import { getDropOffFilteredTimeSlots } from '../filters/dropOff.js';
import { getEligibleTimeSlots, filterTimeSlotsByLeadTime, removeEmptyDateObjects } from '../filters/helpers/utilities.js';
import { getNurseFilteredTimeSlots } from '../filters/nurse.js';
import { getCareSendSupplyFilteredTimeSlots, getOfficeSupplyFilteredTimeSlots } from '../filters/supply.js';
import { flagOn } from '../../variables/index.js';

/**
 * Clears the places data. This is important if someone enters a new address
 * without exiting the scheduling flow and is scheduling a B&M visit.
 */
const clearPlaces = () => {
    const store = getStore();
    store.commit('places/RESET_PLACES');
    store.commit('places/RESET_PLACE_GROUPS');
};
/**
 * Get an office from firebase.
 * @param officeID - The ID of the office to get.
 * @returns The office or null if the office does not exist.
 */
const getOffice = async (officeID) => getValueOnce(getOfficePath(officeID));
/**
 * Return the patient supply owner info by ownerID by timestamp
 * given a service region group
 */
const getPatientShipmentInfo = async (serviceRegionGroupID) => {
    var _a;
    const path = `${DbRef.PATIENT_SHIPMENT_INFO_BY_SERVICE_REGION_GROUP}/${serviceRegionGroupID}`;
    return (_a = (await getValueOnce(path))) !== null && _a !== void 0 ? _a : {};
};
/**
 * Return the lab supply shipment info by place group ID by timestamp.
 */
const getLabShipmentInfo = async (serviceRegionGroupID) => {
    var _a;
    const path = `${DbRef.LAB_SHIPMENT_INFO_BY_SERVICE_REGION_GROUP}/${serviceRegionGroupID}`;
    return (_a = (await getValueOnce(path))) !== null && _a !== void 0 ? _a : {};
};
const getDraftWaypointPriceResponse = async (state) => {
    const store = getStore();
    if (!state.draftWaypoint || !state.selectedTimeSlot) {
        throw Error('Missing visit information when processing payment.');
    }
    const timeSlotHelperParams = store.getters['schedulingFlow/getTimeSlotHelperParams'](state.selectedTimeSlot);
    if (!timeSlotHelperParams) {
        throw Error('Missing time slot helper params.');
    }
    const { visitPlaceGroupIDs } = state.localState;
    if (!visitPlaceGroupIDs.length) {
        throw Error('Missing visit place group IDs.');
    }
    const { labShipmentInfo, patientShipmentInfo } = timeSlotHelperParams;
    const draftWaypointPriceResponse = await calculateDraftWaypointVisitPriceRequest({
        draftWaypoint: state.draftWaypoint,
        timeSlot: state.selectedTimeSlot,
        visitPlaceGroupIDs,
        patientShipmentInfo,
        ...(labShipmentInfo && { labShipmentInfo }),
        nurseID: state.suggestedNurseID,
    });
    if (!draftWaypointPriceResponse) {
        throw Error('Missing price information when processing payment.');
    }
    return draftWaypointPriceResponse;
};
/**
 * Will determine the minimum title required to complete the task for a given Procedure or Draft<Procedure>.
 */
const getMinimumOrderedMedicalTitleForDraftWaypoint = () => {
    var _a;
    const store = getStore();
    const schedulingFlowState = store.state.schedulingFlow;
    const procedures = {
        ...schedulingFlowState.draftProcedures,
        ...schedulingFlowState.procedures,
    };
    const tasks = (_a = store.state.variables.variables) === null || _a === void 0 ? void 0 : _a.tasks;
    if (!tasks)
        throw Error('No variables tasks found.');
    return getMinimumTitleForProcedures(Object.values(procedures !== null && procedures !== void 0 ? procedures : {}), tasks);
};
const getOfficeFromProceduresData = (proceduresData) => { var _a, _b; return (_b = (_a = Object.values(proceduresData)[0]) === null || _a === void 0 ? void 0 : _a.orderData) === null || _b === void 0 ? void 0 : _b.officeData; };
const getPatientPays = (proceduresData) => { var _a, _b; return !!((_b = (_a = getOfficeFromProceduresData(proceduresData)) === null || _a === void 0 ? void 0 : _a.bookingPricing) === null || _b === void 0 ? void 0 : _b.paymentEnabled); };
/**
 * Get the scheduling filter layer based on the supply instruction config options
 * by procedure. Return a single SchedulingFilter array with the filters that are
 * enabled. Each filter is enabled if the supply instruction config options
 * includes the filter, except for SITE_COLLECTION and MOBILE_COLLECTION, which
 * are only included if all the procedures have it enabled.
 */
const getSchedulingFilterLayer = (supplyInstructionConfigOptionsByProcedure) => {
    const schedulingFilter = [];
    let includeSiteCollection = true;
    let includeMobileCollection = true;
    Object.entries(supplyInstructionConfigOptionsByProcedure !== null && supplyInstructionConfigOptionsByProcedure !== void 0 ? supplyInstructionConfigOptionsByProcedure : {}).forEach(([_, supplyInstructionConfigOptions]) => {
        var _a;
        const layer = Object.values((_a = supplyInstructionConfigOptions.schedulingFilters) !== null && _a !== void 0 ? _a : {});
        if (Array.isArray(layer)) {
            if (layer.includes(SchedulingFilter.SUPPLY) || layer.includes(SchedulingFilter.SUPPLIES_SHIPPED_BY_CARESEND)) {
                schedulingFilter.push(SchedulingFilter.SUPPLIES_SHIPPED_BY_CARESEND);
            }
            if (layer.includes(SchedulingFilter.SUPPLIES_SHIPPED_BY_OFFICE)) {
                schedulingFilter.push(SchedulingFilter.SUPPLIES_SHIPPED_BY_OFFICE);
            }
            if (layer.includes(SchedulingFilter.NURSE)) {
                schedulingFilter.push(SchedulingFilter.NURSE);
            }
            if (layer.includes(SchedulingFilter.DROP_OFF)) {
                schedulingFilter.push(SchedulingFilter.DROP_OFF);
            }
            if (!layer.includes(SchedulingFilter.SITE_COLLECTION)) {
                includeSiteCollection = false;
            }
            if (!layer.includes(SchedulingFilter.MOBILE_COLLECTION)) {
                includeMobileCollection = false;
            }
        }
    });
    if (includeSiteCollection) {
        schedulingFilter.push(SchedulingFilter.SITE_COLLECTION);
    }
    if (includeMobileCollection) {
        schedulingFilter.push(SchedulingFilter.MOBILE_COLLECTION);
    }
    return deduplicateArray(schedulingFilter);
};
/**
 * Return the time slots by date from firebase for a given service region
 * based on the minimum title
 */
const getTimeSlotsByDate = async (serviceRegionID, minimumTitle) => {
    var _a;
    const titleIndex = orderedMedicalTitleRanking.indexOf(minimumTitle);
    const titlesFromAndIncludingMinimum = orderedMedicalTitleRanking.slice(undefined, titleIndex + 1);
    const timeSlotPath = `${DbRef.TIME_SLOTS_BY_SERVICE_REGION}/${serviceRegionID}`;
    const allTimeSlotsByDate = await getValuesOnce([timeSlotPath]);
    const timeSlotsByDate = {};
    Object.entries((_a = allTimeSlotsByDate[0]) !== null && _a !== void 0 ? _a : {}).forEach(([date, timeSlots]) => {
        Object.entries(timeSlots).forEach(([time, timeSlot]) => {
            const { availableNurses } = timeSlot;
            const filteredNurses = Object.entries(availableNurses).filter(([_, nurseInfo]) => titlesFromAndIncludingMinimum.includes(nurseInfo.medicalTitle));
            if (filteredNurses.length > 0) {
                timeSlotsByDate[date] = {
                    ...timeSlotsByDate[date],
                    [time]: {
                        ...timeSlot,
                        availableNurses: Object.fromEntries(filteredNurses),
                    },
                };
            }
        });
    });
    return timeSlotsByDate;
};
/**
 * Filters time slots based on the scheduling filter
 */
const getFilteredTimeSlotInfo = (timeSlotsByDate, patientSupplyOwnerInfo, labShipmentInfo) => {
    var _a, _b;
    const store = getStore();
    let timeSlots = timeSlotsByDate;
    const { draftWaypoint, procedures, schedulingConfig, schedulingFilter, supplyInstructionConfigOptions, } = store.state.schedulingFlow;
    const leadTime = (_b = (_a = schedulingConfig === null || schedulingConfig === void 0 ? void 0 : schedulingConfig.baseScheduling) === null || _a === void 0 ? void 0 : _a.leadTime) !== null && _b !== void 0 ? _b : 0;
    timeSlots = filterTimeSlotsByLeadTime(timeSlots, leadTime);
    if (!draftWaypoint)
        throw Error('Missing draft waypoint');
    if (!procedures)
        throw Error('Missing procedures');
    if (!schedulingConfig)
        throw Error('Missing scheduling config');
    if (!schedulingFilter || !schedulingFilter.length)
        throw Error('Missing scheduling filter');
    if (!supplyInstructionConfigOptions)
        throw Error('Missing supply instruction config options');
    // If mobile collection is not enabled, the timeslots are not returned
    const isMobileCollectionDisabled = schedulingFilter.includes(SchedulingFilter.DROP_OFF)
        && !schedulingFilter.includes(SchedulingFilter.MOBILE_COLLECTION);
    store.commit('schedulingFlow/SET_IS_MOBILE_ELIGIBLE', !isMobileCollectionDisabled);
    if (schedulingFilter.includes(SchedulingFilter.DROP_OFF)) {
        const siteCollectionEnabledByProcedures = schedulingFilter.includes(SchedulingFilter.SITE_COLLECTION);
        const siteCollectionEnabledByOffice = schedulingConfig.dropOffScheduling.labDrawSiteCollection;
        timeSlots = getDropOffFilteredTimeSlots(timeSlots, labShipmentInfo, supplyInstructionConfigOptions, siteCollectionEnabledByProcedures, draftWaypoint, procedures, siteCollectionEnabledByOffice);
    }
    if (!isMobileCollectionDisabled) {
        timeSlots = getCollectionFilteredTimeSlots(timeSlots);
        // It is imporatnt to filter the nurses first because we need to know which
        // nurses are available for the supply filter.
        if (schedulingFilter.includes(SchedulingFilter.NURSE)) {
            timeSlots = getNurseFilteredTimeSlots(timeSlots, schedulingConfig);
        }
        /**
         * At the moment, we can only have one of the two supply filters enabled.
         * We will eventually support both filters enabled at the same time.
         */
        if (schedulingFilter.includes(SchedulingFilter.SUPPLIES_SHIPPED_BY_CARESEND)) {
            const procedureIDs = Object.keys(procedures !== null && procedures !== void 0 ? procedures : {});
            const { warehouseFallback } = schedulingConfig.supplyScheduling;
            timeSlots = getCareSendSupplyFilteredTimeSlots(timeSlots, patientSupplyOwnerInfo, supplyInstructionConfigOptions, draftWaypoint, procedureIDs, warehouseFallback);
        }
        else if (schedulingFilter.includes(SchedulingFilter.SUPPLIES_SHIPPED_BY_OFFICE)) {
            timeSlots = getOfficeSupplyFilteredTimeSlots(timeSlots, supplyInstructionConfigOptions);
        }
        timeSlots = removeEmptyDateObjects(timeSlots);
    }
    else {
        timeSlots = {};
    }
    return timeSlots;
};
const isSchedulingDryRun = (draftWaypoint) => {
    var _a;
    const isDryRunDueToFeatureFlag = flagOn(FeatureFlag.SCHEDULING_FLOW_DRY_RUN);
    const isDryRunDueToDebugProperty = !!((_a = draftWaypoint.debug) === null || _a === void 0 ? void 0 : _a.dryRun);
    return isDryRunDueToFeatureFlag || isDryRunDueToDebugProperty;
};
const getTextWithDryRun = (title, { store }) => {
    const isDryRun = store.state.schedulingFlow.draftWaypoint
        ? isSchedulingDryRun(store.state.schedulingFlow.draftWaypoint)
        : false;
    if (!isDryRun)
        return title;
    return title ? `${title} (dry run)` : 'Dry run enabled';
};
const trackTimeSlotRefresh = async (previouslyAvailableTimeSlots, timeRefreshStart, lastFetchedAt) => {
    const store = getStore();
    const { availableTimeSlots, draftWaypoint, timeSlotRefreshInterval, } = store.state.schedulingFlow;
    if (!draftWaypoint)
        return;
    const getNumberOfTimeSlots = (timeSlots) => Object.values(timeSlots).reduce((count, slotsForDate) => count + Object.values(slotsForDate).length, 0);
    const availableTS = getEligibleTimeSlots(availableTimeSlots);
    const numberOfTimeSlots = getNumberOfTimeSlots(availableTS);
    const previousTS = getEligibleTimeSlots(previouslyAvailableTimeSlots);
    const numberOfPreviousTimeSlots = getNumberOfTimeSlots(previousTS);
    const secondsSinceLastFetch = lastFetchedAt
        ? (timeRefreshStart - lastFetchedAt) / SECOND
        : undefined;
    // We're using `setTimeout` to defer the execution of the event tracking
    // to improve the user experience.
    setTimeout(async () => {
        trackEvent(store, TrackingEvent.TIME_SLOT_REFRESH, {
            draftWaypointID: draftWaypoint.id,
            numberOfTimeSlots,
            numberOfPreviousTimeSlots,
            secondsSinceLastFetch,
            timeSlotRefreshInterval,
        });
    }, 0);
};
const isTimeSlotEligible = (timeSlot, patientShipmentInfo, labShipmentInfo) => {
    var _a;
    if (!timeSlot)
        return false;
    const date = timeSlot.startDate;
    const time = timeSlot.startTime;
    const timeSlotsObject = {
        [date]: {
            [time]: timeSlot,
        },
    };
    const timeSlotInfo = getFilteredTimeSlotInfo(timeSlotsObject, patientShipmentInfo, labShipmentInfo);
    return ((_a = timeSlotInfo[date]) === null || _a === void 0 ? void 0 : _a[time]) !== undefined;
};
const getAreAllSupplyInstructionsPresent = (proceduresData) => Object.values(proceduresData).every((procedure) => procedure.supplyInstructionConfigOptionsData);

export { clearPlaces, getAreAllSupplyInstructionsPresent, getDraftWaypointPriceResponse, getFilteredTimeSlotInfo, getLabShipmentInfo, getMinimumOrderedMedicalTitleForDraftWaypoint, getOffice, getOfficeFromProceduresData, getPatientPays, getPatientShipmentInfo, getSchedulingFilterLayer, getTextWithDryRun, getTimeSlotsByDate, isSchedulingDryRun, isTimeSlotEligible, trackTimeSlotRefresh };
